import {useEffect, useMemo, useRef, useState} from "react";
import {Skeleton, Stack} from "@mantine/core";
import {dibAppApi} from "../../../dibAppApi/dibAppApi";
import {ResourceError} from "../ResourceError";
import {StatusMessage} from "../StatusMessage";
import {renderNode} from "./renderNodeLegacy";
import {buildTocTree, type TocEntry} from "../tocUtils";
import type {DibDocument} from "../types";
import {TableOfContentsLEGACY} from "./TableOfContentsLEGACY";
import {RelatedContentLEGACY} from "./RelatedContentLEGACY";
import {SupplierInfoLEGACY} from "./SupplierInfoLEGACY";
import {useContentTitle} from "../useContentTitle";
import layoutClasses from "./ContentLayoutLEGACY.module.css";
import articleClasses from "./HtmlArticleLEGACY.module.css";

const DocumentSkeleton = () => (
    <Stack gap="sm" pt="xl">
        <Skeleton height={32} width="60%" mb="md" />
        <Skeleton height={14} width="100%" />
        <Skeleton height={14} width="95%" />
        <Skeleton height={14} width="88%" />
        <Skeleton height={14} width="92%" />
        <Skeleton height={14} width="40%" />
    </Stack>
);

// descroot always carries the `xdesc;` container, so its length is never 0.
// A description only exists when that container has children to render.
const hasDescription = (doc: DibDocument) =>
    doc.descroot.some((entry) => {
        const el = doc.elements[entry.id];
        return (el?.children?.length ?? 0) > 0;
    });

const renderDescription = (doc: DibDocument) =>
    doc.descroot.flatMap((entry) => {
        const el = doc.elements[entry.id];
        if (!el?.children) return [];
        return el.children
            .filter((c): c is {id: string} => "id" in c)
            .map((c) => renderNode(doc, c.id, {}, c.id));
    });

const renderContent = (doc: DibDocument) =>
    doc.root.map((entry) => renderNode(doc, entry.id, {}, entry.id));

const collectTocIds = (entries: TocEntry[]): string[] =>
    entries.flatMap((e) => [e.id, ...collectTocIds(e.children)]);

export const HTMLResourceLEGACY = ({
    resourceId,
    segmentId,
}: {
    resourceId: string;
    segmentId: string | undefined;
}) => {
    const {
        currentData: doc,
        isFetching,
        isError,
        error,
    } = dibAppApi.endpoints.getDocument.useQuery({resourceId, segmentId});

    const articleRef = useRef<HTMLElement>(null);
    const isProgrammaticScroll = useRef(false);
    const [activeId, setActiveId] = useState<string | null>(
        () => window.location.hash.slice(1) || null
    );

    // --- Segment continuity (issue #169) ---
    // currentData (doc) goes undefined the instant the segmentId query arg
    // changes to an uncached key, which is what blanks the TOC and info column
    // today. Retain the last loaded document and keep rendering the chrome from
    // it while a SAME-topic segment fetch is in flight.
    const prevDocRef = useRef<DibDocument | null>(null);
    if (doc) prevDocRef.current = doc;

    // Title from the retained doc so same-topic segment fetches (which blank
    // currentData) don't flicker the title back to "KAI".
    useContentTitle((doc ?? prevDocRef.current)?.name);

    // True only while fetching a new segment of the same topic. topicId is
    // stable across a top-level TOC click (the <Link> only changes segmentId),
    // so a different-topic navigation, a cold load, or an errored fetch all
    // leave this false and fall through to the full skeleton.
    const isIntraTopicFetch =
        !doc && isFetching && prevDocRef.current?.topicId === resourceId;

    // Drives the persistent chrome (TOC + info column). Live doc when present,
    // else the retained doc during an intra-topic fetch. The article keeps using
    // `doc` directly so it still shows the skeleton while the segment loads.
    const chromeDoc = doc ?? (isIntraTopicFetch ? prevDocRef.current : null);

    // Mark the segment being navigated to as active as soon as the URL changes,
    // so the kept-visible TOC highlights the incoming segment during the load.
    // The article isn't rendered yet (no sections for the scroll-spy), and a
    // seg=1 entry is a <Link> that never calls handleTocClick. Scroll-spy
    // refines this once the segment renders.
    useEffect(() => {
        if (segmentId) setActiveId(segmentId);
    }, [segmentId]);

    // Set of section IDs that appear in the TOC. The IntersectionObserver
    // observes only these so non-TOC sections in the article body can't
    // hijack the active highlight.
    const tocIds = useMemo(() => {
        if (!doc) return new Set<string>();
        return new Set(collectTocIds(buildTocTree(doc)));
    }, [doc]);

    // On document (re)load, scroll to the URL hash if present.
    useEffect(() => {
        if (!doc?.access || doc.root.length === 0) return;
        const hash = window.location.hash.slice(1);
        if (!hash) return;
        requestAnimationFrame(() => {
            document.getElementById(hash)?.scrollIntoView({block: "start"});
        });
    }, [doc, resourceId, segmentId]);

    // Seed activeId to the topmost rendered TOC section whenever the
    // article is at the top. The IntersectionObserver's -10%/-70% band can
    // miss short top-of-document sections, leaving the TOC un-highlighted at
    // scrollTop 0 — both on initial load and after the user scrolls back to
    // the top from anywhere in the document. The seed is the first
    // `section[id]` in the article DOM that maps to a TOC entry, NOT the
    // first entry of the whole TOC tree: in a segmented topic the article
    // only renders the current segment's sections, so the tree's first id
    // need not be present here. A scroll listener subsumes the old
    // initial-load-only seeding effect: we run the check once on mount to
    // cover the no-hash open-at-top case (a scroll event doesn't fire just
    // because the listener attaches), and the listener handles every
    // subsequent return-to-top. The initial-mount check is skipped when
    // window.location.hash is set so the hash-driven scroll/highlight wins;
    // after the hash scroll fires, scrollTop is non-zero so the listener
    // can't clobber it. The programmatic-scroll lock is respected so a TOC
    // click that scrolls to the first entry doesn't double-fire.
    useEffect(() => {
        const root = articleRef.current;
        if (!root || tocIds.size === 0) return;

        const seedIfAtTop = () => {
            if (isProgrammaticScroll.current) return;
            if (root.scrollTop !== 0) return;
            const topSection = Array.from(
                root.querySelectorAll<HTMLElement>("section[id]")
            ).find((s) => tocIds.has(s.id));
            if (!topSection) return;
            setActiveId((prev) =>
                prev === topSection.id ? prev : topSection.id
            );
        };

        if (!window.location.hash) seedIfAtTop();

        root.addEventListener("scroll", seedIfAtTop, {passive: true});
        return () => root.removeEventListener("scroll", seedIfAtTop);
    }, [tocIds]);

    // IntersectionObserver scroll-spy: the topmost intersecting TOC section
    // becomes activeId. Suppressed for ~500ms after a programmatic scroll.
    useEffect(() => {
        const root = articleRef.current;
        if (!root || !doc) return;

        const sections = Array.from(
            root.querySelectorAll<HTMLElement>("section[id]")
        ).filter((s) => tocIds.has(s.id));

        const observer = new IntersectionObserver(
            (entries) => {
                if (isProgrammaticScroll.current) return;
                const topMost = entries
                    .filter((e) => e.isIntersecting)
                    .sort(
                        (a, b) =>
                            a.boundingClientRect.top - b.boundingClientRect.top
                    )[0];
                if (topMost) setActiveId(topMost.target.id);
            },
            {root, rootMargin: "-10% 0px -70% 0px", threshold: 0}
        );

        sections.forEach((s) => observer.observe(s));
        return () => observer.disconnect();
    }, [doc, tocIds]);

    const handleTocClick = (id: string) => {
        isProgrammaticScroll.current = true;
        setActiveId(id);
        window.history.replaceState(null, "", `#${id}`);
        document
            .getElementById(id)
            ?.scrollIntoView({behavior: "smooth", block: "start"});
        window.setTimeout(() => {
            isProgrammaticScroll.current = false;
        }, 500);
    };

    return (
        <div className={layoutClasses.page}>
            <nav className={layoutClasses.toc}>
                {chromeDoc && (
                    <TableOfContentsLEGACY
                        document={chromeDoc}
                        activeId={activeId}
                        onTocClick={handleTocClick}
                    />
                )}
            </nav>

            <article ref={articleRef} className={layoutClasses.article}>
                {isFetching && !doc && <DocumentSkeleton />}
                {isError && <ResourceError error={error} />}
                {doc && !doc.access && (
                    <StatusMessage
                        title="Access denied"
                        detail="Your subscription does not include access to this document."
                    />
                )}
                {doc?.access && doc.root.length === 0 && (
                    <StatusMessage
                        title="Empty document"
                        detail="This document has no content to display."
                    />
                )}
                {doc?.access && doc.root.length > 0 && (
                    <>
                        <h1 className={articleClasses.title}>{doc.name}</h1>
                        {hasDescription(doc) && (
                            <div className={articleClasses.description}>
                                {renderDescription(doc)}
                            </div>
                        )}
                        <div className={articleClasses.content}>
                            {renderContent(doc)}
                        </div>
                    </>
                )}
            </article>

            <aside className={layoutClasses.related}>
                {chromeDoc && (
                    <>
                        <SupplierInfoLEGACY document={chromeDoc} />
                        <RelatedContentLEGACY document={chromeDoc} />
                    </>
                )}
            </aside>
        </div>
    );
};
